<template>
  <div class="home-content">
    <div v-if="showSeparateVisitTabs" class="tab-container">
      <div class="custom-tabs">
        <div
          class="tab-item"
          :class="{ active: active === '0' }"
          @click="changeTab('0')"
        >
          {{ $t('visit') }}
        </div>
        <div
          class="tab-item"
          :class="{ active: active === '1' }"
          @click="changeTab('1')"
        >
          {{ $t('co_visit') }}
        </div>
      </div>
    </div>

    <div
      ref="scrollContainer"
      class="scroll-container"
      :class="{ 'with-tabs': showSeparateVisitTabs }"
      @scroll="handleScroll"
    >
      <Loading
        v-if="loading && currentPage === 0"
        style="text-align: center; margin-top: 120px; margin-bottom: 40px"
        type="spinner"
        color="#aa341b"
      />
      <div v-if="!loading && visitDraftCount > 0" class="draft-banner">
        <div class="draft-banner-text">{{ draftBannerText }}</div>
      </div>
      <div v-if="loading && currentPage > 0" ref="topLoadingMore" class="loading-more loading-more-top">
        <Loading type="spinner" color="#aa341b" size="20px" />
      </div>
      <div v-for="(item, index) in lastList" :key="index" class="date-group">
        <div class="date-title">{{ item.time }}</div>
        <div class="list-items">
          <VisitListItem
            v-for="(sonItem, sonIndex) in item.list"
            :key="sonIndex"
            :color="changeColor(sonItem)"
            :item="sonItem"
          />
        </div>
      </div>
      <Empty
        v-if="!lastList.length && !loading"
        :description="showSeparateVisitTabs && active === '1' ? $t('no_co_visit') : $t('no_visit')"
        image="/image/empty.png"
        style="padding-top: 80px;"
      />
      <div style="height: 100px;"></div>
    </div>

    <div class="bottom-card-container">
      <div class="bottom-card">
        <div v-if="!loading && todayStatusText" class="status-text">{{ todayStatusText }}</div>
        <Button
          block
          round
          color="linear-gradient(165deg, #d2563d, #aa341b)"
          icon="plus"
          class="action-button"
          @click="
            showSeparateVisitTabs && active === '1'
              ? $router.push(`visit-create?isCoVisit=${active}`)
              : $router.push(`visit-create`)
          "
        >
          <template v-if="isUnifiedVisitList">
            {{ $t('hcp_visit_create_joint_action') }}
          </template>
          <template v-else>
            {{ $t('new_action') }} {{ showSeparateVisitTabs && active === '1' ? $t('co_visit') : $t('visit') }}
          </template>
        </Button>
      </div>
    </div>
  </div>
</template>
<script>
import VisitListItem from './components/visitListItem.vue';
import {Button, Empty, Loading} from 'vant';
import {fetchPhysicianMrCalls} from '@/api/Physicians';
import {
  getLocalStorage,
  isMR,
  isGBA,
} from '@/utils/index';
import {buildVisitDraftListItem, listVisitDrafts} from '@/utils/visitDraft';

export default {
  components: {
    VisitListItem,
    Button,
    Empty,
    Loading,
  },
  data() {
    return {
      active: '0',
      lastList: [],
      loading: false,
      finished: false,
      pageSize: 30,
      currentPage: 0,
      hasMore: true,
      lastScrollHeight: 0,
      visitDraftCount: 0,
    };
  },
  computed: {
    todayStatusText() {
      const hasToday = this.lastList.some(item => item.time === this.$t('today'));
      const noTodayText = this.showSeparateVisitTabs && this.active === '1'
        ? this.$t('no_co_visit_today')
        : this.$t('no_visit_today');

      if (this.lastList.length === 0 && !this.loading) return noTodayText;
      return hasToday ? '' : noTodayText;
    },
    isMR() {
      return isMR();
    },
    isUnifiedVisitList() {
      return isGBA();
    },
    showSeparateVisitTabs() {
      return !this.isMR && !this.isUnifiedVisitList;
    },
    draftBannerText() {
      const typeText = this.showSeparateVisitTabs && this.active === '1'
        ? this.$t('co_visit')
        : this.$t('visit');
      return this.$t('hcp_visit_list_draft_banner', {
        count: this.visitDraftCount,
        type: typeText,
      });
    },
    dateLocale() {
      return this.$i18n.locale === 'EN' ? 'en-US' : 'zh-CN';
    },
  },
  created() {
    // Restore active tab from storage if present
    const savedActive = sessionStorage.getItem('visit_list_active_tab');
    if (savedActive && this.showSeparateVisitTabs) {
      this.active = savedActive;
    }
    this.updateVisitDraftCount();
    this.$root.$on('updateVisitDraftCount', this.updateVisitDraftCount);
    this.getPhysicianMrCalls();
  },
  mounted() {},
  destroyed() {
    this.$root.$off('updateVisitDraftCount', this.updateVisitDraftCount);
  },
  methods: {
    getLocalStorage,
    updateVisitDraftCount() {
      this.visitDraftCount = listVisitDrafts({
        userId: getLocalStorage('user_id'),
        mode: isGBA() ? 'gba' : 'hcp',
        isCoVisit: this.showSeparateVisitTabs ? this.active === '1' : undefined,
      }).length;
    },
    enrichVisitItem(item) {
      return {
        ...item,
        call_summary: item.call_summary || '',
      };
    },
    async getPhysicianMrCalls() {
      if (this.finished || this.loading) return;

      this.loading = true;
      const userId = getLocalStorage('user_id');
      const filter = this.isUnifiedVisitList
        ? {visible_to_user_id: userId}
        : {
          is_co_call: Number(this.active),
          call_user_id: userId,
          role_id: getLocalStorage('role_id'),
        };
      const res = await fetchPhysicianMrCalls(
        filter,
        this.currentPage * this.pageSize,
        this.pageSize,
      );

      if (!res.value || res.value.length < this.pageSize) {
        this.finished = true;
      }

      const changeArr = this.convertTo2DArray(
        this.mergeDrafts(res.value || [], this.currentPage === 0),
      );
      this.lastList = [...changeArr, ...this.lastList];

      if (this.currentPage === 0) {
        this.$nextTick(() => {
          const container = this.$refs.scrollContainer;
          container.scrollTop = container.scrollHeight;

          this.currentPage++;
          this.loading = false;
        });
      } else {
        this.$nextTick(() => {
          const container = this.$refs.scrollContainer;
          const topLoadingMore = this.$refs.topLoadingMore;
          const loadingHeight = topLoadingMore ? topLoadingMore.offsetHeight : 0;
          const scrollTop = Math.max(container.scrollHeight - this.lastScrollHeight - loadingHeight, 0);
          container.scrollTop = scrollTop;
          container.scrollTo({
            top: scrollTop,
            behavior: 'auto',
          });

          this.currentPage++;
          this.loading = false;
        });
      }
    },
    convertTo2DArray(inputArray) {
      if (!Array.isArray(inputArray) || !inputArray.length) return [];
      const dateMap = {};

      for (let i = inputArray.length - 1; i >= 0; i--) {
        const element = this.enrichVisitItem(inputArray[i]);
        const callTime = element.call_time.split('T')[0]; // 截取日期部分
        const currentDate = new Date().toISOString().split('T')[0]; // 当前日期
        const nextDate = new Date(Date.now() + 24 * 60 * 60 * 1000)
          .toISOString()
          .split('T')[0]; // 明天的日期

        let time;
        if (callTime !== currentDate && callTime !== nextDate) {
          time = new Date(callTime).toLocaleDateString(this.dateLocale, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          }); // 格式化日期为 "YYYY年MM月DD日"
        }
        if (callTime === currentDate) {
          time = this.$t('today');
        }
        if (callTime === nextDate) {
          time = this.$t('tomorrow');
        }

        if (Object.prototype.hasOwnProperty.call(dateMap, time)) {
          dateMap[time].list.push(element);
        } else {
          dateMap[time] = {time, list: [element], crm_status: element.crm_status};
        }
      }

      const resultArray = Object.values(dateMap);
      return resultArray;
    },
    mergeDrafts(serverItems = [], includeDrafts = false) {
      const drafts = listVisitDrafts({
        userId: getLocalStorage('user_id'),
        mode: isGBA() ? 'gba' : 'hcp',
        isCoVisit: this.showSeparateVisitTabs ? this.active === '1' : undefined,
      });
      const draftSourceIds = new Set(
        drafts
          .map((draft) => draft.sourceCallId ? String(draft.sourceCallId) : '')
          .filter(Boolean),
      );

      const mergedMap = new Map();
      serverItems.forEach((item) => {
        if (!includeDrafts && draftSourceIds.has(String(item.physician_mr_call_id))) {
          return;
        }
        mergedMap.set(String(item.physician_mr_call_id), {
          ...item,
          is_draft: false,
          draft_id: '',
          feedback_count: Array.isArray(item.physician_mr_call__physician_mr_call_key_feedbacks)
            ? item.physician_mr_call__physician_mr_call_key_feedbacks.length
            : 0,
          has_feedback: Array.isArray(item.physician_mr_call__physician_mr_call_key_feedbacks)
            ? item.physician_mr_call__physician_mr_call_key_feedbacks.length > 0
            : false,
        });
      });

      if (!includeDrafts) {
        return Array.from(mergedMap.values()).sort((a, b) => {
          const timeA = new Date(a.call_time || a.draft_updated_at || 0).getTime();
          const timeB = new Date(b.call_time || b.draft_updated_at || 0).getTime();
          return timeB - timeA;
        });
      }

      drafts.forEach((draft) => {
        const sourceId = draft.sourceCallId ? String(draft.sourceCallId) : '';
        const draftItem = buildVisitDraftListItem(draft);
        if (sourceId && mergedMap.has(sourceId)) {
          const serverItem = mergedMap.get(sourceId);
          mergedMap.set(sourceId, {
            ...serverItem,
            ...draftItem,
            physician: draftItem.physician?.physician_name
              ? draftItem.physician
              : serverItem.physician,
            physician_mr_call__physician_mr_call_companions:
              draftItem.physician_mr_call__physician_mr_call_companions?.length > 0
                ? draftItem.physician_mr_call__physician_mr_call_companions
                : serverItem.physician_mr_call__physician_mr_call_companions,
            call_time: draftItem.call_time || serverItem.call_time,
          });
        } else {
          mergedMap.set(draftItem.draft_id, draftItem);
        }
      });

      return Array.from(mergedMap.values()).sort((a, b) => {
        const timeA = new Date(a.call_time || a.draft_updated_at || 0).getTime();
        const timeB = new Date(b.call_time || b.draft_updated_at || 0).getTime();
        return timeB - timeA;
      });
    },
    changeTab(val) {
      this.active = val;
      sessionStorage.setItem('visit_list_active_tab', val);
      this.updateVisitDraftCount();
      this.lastList = [];
      this.currentPage = 0;
      this.finished = false;
      this.hasMore = true;
      this.getPhysicianMrCalls();
    },
    changeColor(item) {
      if (item.is_draft) {
        return '#f0b95b';
      }
      const crmStatus = item.crm_status;
      if (crmStatus === 'Submitted_vod') {
        return '#b2b2b2';
      } else if (crmStatus === 'Planned_vod') {
        return '#f5b496';
      } else if (crmStatus === 'Saved_vod') {
        return '#aa341b';
      } else {
        return '#e4e4e4';
      }
    },
    handleScroll() {
      const scrollContainer = this.$refs.scrollContainer;
      const {scrollHeight, scrollTop, clientHeight} = scrollContainer;
      if (scrollTop === 0 && this.hasMore) {
        this.lastScrollHeight = scrollHeight;
        this.getPhysicianMrCalls(true);
      }
    },
  },
};
</script>
<style scoped lang="scss">
.home-content {
  // min-height: calc(100% - 40px);
  // padding: 10px;
  // background: #f9f9f9;

  .tab-container {
    padding: 8px 0;
    display: flex;
    justify-content: center;
    position: sticky;
    top: 0;
    z-index: 10;
    background: linear-gradient(180deg, #ffffff 0%, #f4f4f4 100%);
  }

  .custom-tabs {
    display: flex;
    background: #eef1f6;
    padding: 4px;
    border-radius: 20px;

    .tab-item {
      padding: 6px 32px;
      border-radius: 16px;
      font-size: 14px;
      color: #666;
      font-weight: 500;
      transition: all 0.3s;
      cursor: pointer;

      &.active {
        background: #fff;
        color: #aa341b;
        box-shadow: 0 2px 8px rgba(0,0,0,0.08);
        font-weight: bold;
      }
    }
  }

  .scroll-container {
    height: calc(100vh - 190px);
    overflow-y: scroll;
    padding: 0 5px 140px;

    &.with-tabs {
      height: calc(100vh - 240px);
    }
  }

  .draft-banner {
    position: sticky;
    top: 34px;
    z-index: 7;
    margin: 8px 8px 4px;
    padding: 7px 10px;
    border-radius: 12px;
    background: linear-gradient(180deg, #fff7ea 0%, #fffdf8 100%);
    border: 1px solid #f3d49a;
    box-shadow: 0 4px 12px rgba(170, 52, 27, 0.05);
    backdrop-filter: blur(8px);
  }

  .draft-banner-text {
    font-size: 12px;
    line-height: 1.35;
    color: #8b5b0c;
    text-align: left;
  }

  .date-group {
    padding-top: 22px;
  }

  .loading-more {
    display: flex;
    justify-content: center;
    padding: 12px 0;
  }

  .loading-more-top {
    position: sticky;
    top: 0;
    z-index: 8;
    background: #f3f5f9;
  }

  .date-title {
    font-size: 18px;
    font-weight: 400;
    color: #4a4a4a;
    text-align: left;
    margin-bottom: 14px;
    padding: 10px 8px 14px;
    letter-spacing: 0.2px;
    position: sticky;
    top: 0;
    z-index: 6;
    background: #f3f5f9;
  }

  .bottom-card-container {
    position: fixed;
    width: 90%;
    bottom: 80px;
    bottom: calc(80px + constant(safe-area-inset-bottom));
    bottom: calc(80px + env(safe-area-inset-bottom));
    left: 5%;
    right: 5%;
    z-index: 11;

    .bottom-card {
      background: rgba(255, 255, 255, 0.95);
      backdrop-filter: blur(10px);
      border-radius: 20px;
      padding: 16px;
      box-shadow: 0 8px 32px rgba(0,0,0,0.12);
      text-align: center;
      border: 1px solid rgba(255,255,255,0.5);

      .status-text {
        font-size: 14px;
        color: #666;
        margin-bottom: 12px;
        font-weight: 500;
      }

      .action-button {
        height: 44px;
        font-size: 16px;
        font-weight: 600;
        box-shadow: 0 6px 16px rgba(170, 52, 27, 0.25);
        border: none;
      }
    }
  }
}
</style>
